/**
 * Detect the traffic source of the current visitor based on URL UTM params and document.referrer.
 * Returns a compact, safe, lowercase string suitable for analytics storage.
 *
 * Priority:
 *   1. ?utm_source=X (tracked links)
 *   2. Known referrer domains (google / bing / ddg / twitter / facebook / instagram / tiktok / reddit / youtube / linkedin / pinterest / hackernews / producthunt)
 *   3. Other external referrers → the bare domain (e.g. "example.com")
 *   4. Empty referrer → "direct"
 *   5. Same-origin referrer → "internal"
 */
export function getTrafficSource(): string {
  try {
    // 1. UTM source wins
    const params = new URLSearchParams(window.location.search);
    const utm = params.get('utm_source');
    if (utm) return utm.toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 40) || 'unknown';

    const ref = (document.referrer || '').toLowerCase();
    if (!ref) return 'direct';

    let host = '';
    try { host = new URL(document.referrer).hostname.toLowerCase().replace(/^www\./, ''); } catch { return 'direct'; }

    // 5. Same origin (internal nav) — don't count as a new acquisition channel
    try {
      const here = window.location.hostname.toLowerCase().replace(/^www\./, '');
      if (host === here) return 'internal';
    } catch { /* noop */ }

    // 2. Known channels
    const channels: Array<[RegExp, string]> = [
      [/(^|\.)google\./, 'google'],
      [/(^|\.)bing\./, 'bing'],
      [/(^|\.)duckduckgo\./, 'duckduckgo'],
      [/(^|\.)yandex\./, 'yandex'],
      [/(^|\.)baidu\./, 'baidu'],
      [/(^|\.)(twitter|x)\.com/, 'twitter'],
      [/(^|\.)t\.co/, 'twitter'],
      [/(^|\.)facebook\./, 'facebook'],
      [/(^|\.)fb\./, 'facebook'],
      [/(^|\.)instagram\./, 'instagram'],
      [/(^|\.)tiktok\./, 'tiktok'],
      [/(^|\.)reddit\./, 'reddit'],
      [/(^|\.)youtube\./, 'youtube'],
      [/(^|\.)youtu\.be/, 'youtube'],
      [/(^|\.)linkedin\./, 'linkedin'],
      [/(^|\.)pinterest\./, 'pinterest'],
      [/(^|\.)(news\.)?ycombinator\.com/, 'hackernews'],
      [/(^|\.)producthunt\./, 'producthunt'],
      [/(^|\.)whatsapp\./, 'whatsapp'],
      [/(^|\.)telegram\./, 'telegram'],
      [/(^|\.)discord\./, 'discord'],
    ];
    for (const [re, name] of channels) if (re.test(host)) return name;

    // 3. Fallback: bare host (trimmed + sanitized)
    return host.replace(/[^a-z0-9.-]/g, '').slice(0, 40) || 'unknown';
  } catch {
    return 'unknown';
  }
}
